import { STATUS } from "@shared/types";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FolderCog,
  Gauge,
  PauseCircle,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/empty-state";
import { GroupsDialog } from "@/components/monitors/groups-dialog";
import { MonitorEventTable } from "@/components/monitors/event-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { StatusDot } from "@/components/status-dot";
import { UptimeBars } from "@/components/uptime-bars";
import { useEvents, useMonitors } from "@/lib/queries";
import { formatLatency, formatUptime } from "@/lib/utils";

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-28 rounded-xl" />
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-44 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const monitorsQuery = useMonitors();
  const eventsQuery = useEvents(25);
  const monitors = monitorsQuery.data ?? [];
  const activeMonitors = monitors.filter(monitor => monitor.active);
  const downMonitors = activeMonitors.filter(monitor => monitor.currentStatus === STATUS.DOWN);
  const uptimeValues = activeMonitors
    .map(monitor => monitor.uptime24h)
    .filter((value): value is number => value != null);
  const avgUptime =
    uptimeValues.length > 0
      ? uptimeValues.reduce((total, value) => total + value, 0) / uptimeValues.length
      : null;

  const stats = [
    {
      label: "Up",
      value: activeMonitors.filter(monitor => monitor.currentStatus === STATUS.UP).length,
      icon: CheckCircle2,
      className: "text-status-up",
    },
    {
      label: "Down",
      value: downMonitors.length,
      icon: downMonitors.length > 0 ? AlertTriangle : Activity,
      className: downMonitors.length > 0 ? "text-status-down" : "text-muted-foreground",
      cardClassName: downMonitors.length > 0 ? "border-status-down/30 bg-status-down/5" : "",
    },
    {
      label: "Paused",
      value: monitors.filter(monitor => !monitor.active).length,
      icon: PauseCircle,
      className: "text-status-paused",
    },
    {
      label: "Avg uptime (24h)",
      value: formatUptime(avgUptime),
      icon: Gauge,
      className: "text-status-up",
    },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-6xl mx-auto w-full">
      <PageHeader
        title="Dashboard"
        description="A live view of every monitor and recent status change."
        actions={
          <>
            <GroupsDialog
              trigger={
                <Button type="button" variant="outline">
                  <FolderCog className="size-4" />
                  Manage groups
                </Button>
              }
            />
            <Button asChild>
              <Link to="/monitors/new">
                <Plus className="size-4" />
                New monitor
              </Link>
            </Button>
          </>
        }
      />

      {monitorsQuery.isLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <section aria-label="Monitor overview" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {stats.map(stat => {
              const Icon = stat.icon;
              return (
                <Card key={stat.label} className={`gap-3 py-5 ${stat.cardClassName ?? ""}`}>
                  <CardHeader className="flex grid-cols-none flex-row items-center justify-between px-5">
                    <CardTitle className="text-sm font-medium text-muted-foreground">{stat.label}</CardTitle>
                    <Icon className={`size-4 ${stat.className}`} />
                  </CardHeader>
                  <CardContent className="px-5">
                    <p className="text-2xl font-semibold tabular-nums">{stat.value}</p>
                  </CardContent>
                </Card>
              );
            })}
          </section>

          {downMonitors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>
                {downMonitors.length} {downMonitors.length === 1 ? "monitor is" : "monitors are"} down
              </AlertTitle>
              <AlertDescription className="flex flex-row flex-wrap gap-x-3 gap-y-1">
                {downMonitors.map(monitor => (
                  <Link key={monitor.id} to={`/monitors/${monitor.id}`} className="font-medium underline underline-offset-4">
                    {monitor.name}
                  </Link>
                ))}
              </AlertDescription>
            </Alert>
          )}

          <section aria-labelledby="monitors-heading">
            <div className="mb-3 flex items-center justify-between">
              <h2 id="monitors-heading" className="text-base font-semibold">Monitors</h2>
              <span className="text-xs text-muted-foreground tabular-nums">{monitors.length} total</span>
            </div>
            {monitors.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No monitors yet"
                description="Create your first monitor to start collecting uptime and response-time data."
                action={
                  <Button asChild>
                    <Link to="/monitors/new">
                      <Plus className="size-4" />
                      New monitor
                    </Link>
                  </Button>
                }
              />
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {monitors.map(monitor => (
                  <Link key={monitor.id} to={`/monitors/${monitor.id}`} className="group rounded-xl outline-none">
                    <Card className="h-full gap-5 py-5 transition group-hover:border-ring group-hover:ring-2 group-hover:ring-ring/25 group-focus-visible:ring-2 group-focus-visible:ring-ring">
                      <CardHeader className="grid-cols-[1fr_auto] px-5">
                        <div className="flex min-w-0 items-center gap-2">
                          <StatusDot
                            status={monitor.currentStatus}
                            paused={!monitor.active}
                            pulse={monitor.active && monitor.currentStatus === STATUS.UP}
                          />
                          <CardTitle className="truncate text-sm">{monitor.name}</CardTitle>
                        </div>
                        <StatusBadge status={monitor.currentStatus} paused={!monitor.active} />
                      </CardHeader>
                      <CardContent className="space-y-4 px-5">
                        <UptimeBars beats={monitor.lastBeats} slots={30} className="h-5" />
                        <div className="flex items-center justify-between gap-3 border-t pt-4 text-xs">
                          <div className="flex min-w-0 items-center gap-3 text-muted-foreground">
                            <span className="font-medium text-foreground tabular-nums">
                              {formatUptime(monitor.uptime24h)}
                            </span>
                            <span className="flex items-center gap-1 tabular-nums">
                              <Clock3 className="size-3" />
                              {formatLatency(monitor.lastLatency)}
                            </span>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[10px] font-mono uppercase">
                            {monitor.type}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Card className="gap-3">
        <CardHeader>
          <CardTitle className="text-base">Recent events</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {eventsQuery.isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <MonitorEventTable events={eventsQuery.data ?? []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
